import React from 'react';
import SortByFilter from './SortByFilter';
import ProjectTypeFilter from './ProjectTypeFilter';
import Pagination from '../Pagination';
import NoDataFound from '../NoDataFound';
import { formatValue } from '../../utils/Helper';
import humanizeDuration from 'humanize-duration';
import TableComponent from '../TableComponent';
import { DateTime } from 'luxon';

const ProjectListingRootComponent = ({
  projectLists,
  searchData,
  filterData,
  setFilterData,
  isDropDownList,
  isPagination,
  isListLoader,
}) => {
  const handlePagination = (current) => {
    setFilterData((prev) => ({
      ...prev,
      page: current,
    }));
  };

  const tableConfig = {
    columns: {
      issue: {
        title: 'Issue',
        render: (data) => {
          return (
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-900">
                {data.projectDetail?.name || 'NA'}
              </span>

              {data.is_repaid && (
                <span className="mt-1 px-1.5 py-[2px] rounded-sm text-[10px] text-white font-semibold bg-[#97c05b] w-fit">
                  REPAID
                </span>
              )}
            </div>
          );
        },
      },
      image: {
        title: 'Image',
        render: (data) => {
          return (
            <img
              src={
                data.projectDetail?.project_image || '/img/project_image.png'
              }
              alt="Project"
              className="w-[50px] h-[50px]"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/img/project_image.png';
              }}
            />
          );
        },
      },
      amount_owned: {
        title: 'Amount Owned',
        render: (data) => {
          return data?.quantity || 0;
        },
      },
      invested: {
        title: 'Invested',
        render: (data) => {
          return data?.invested_amount
            ? formatValue(data?.invested_amount, {
                decimalPlaces: 2,
              })
            : 'NA';
        },
      },
      interest_rate: {
        title: 'Interest Rate',
        render: (data) => {
          return data?.projectDetail?.interest_rate
            ? `${data?.projectDetail?.interest_rate}%`
            : 'NA';
        },
      },
      e_apy: {
        title: 'E.APY',
        render: (data) => {
          return (
            <span>
              {data?.projectDetail?.expected_apy
                ? `${data?.projectDetail?.expected_apy}%`
                : 'NA'}
            </span>
          );
        },
      },
      maturity_date: {
        title: 'Maturity Date',
        render: (data) => {
          return (
            <span>
              {data?.projectDetail?.maturity_date
                ? data?.projectDetail?.maturity_date
                : 'NA'}
            </span>
          );
        },
      },
      lifespan: {
        title: 'Lifespan',
        render: (data) => {
          const lifeSpanTo = data?.projectDetail?.life_span_to;

          if (!lifeSpanTo) {
            return 'NA';
          }

          const now = DateTime.local();
          const end = DateTime.fromISO(lifeSpanTo);

          if (now > end) {
            return 'Ended';
          }

          const diff = end.diff(now).as('milliseconds');
          const humanizedDiff = humanizeDuration(diff, {
            largest: 2,
            round: true,
          });

          return humanizedDiff;
        },
      },
    },
    data: projectLists,
  };

  return (
    <div className="flex flex-col relative w-full sm:rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <div>
          <ProjectTypeFilter searchData={searchData} filterData={filterData} />
        </div>
        <div>
          <SortByFilter
            value={filterData.sortBy}
            dropdownList={isDropDownList}
            filterData={filterData}
            setFilterData={setFilterData}
            setPage={(page) => handlePagination(page)}
            fieldName="sortBy"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <TableComponent
          tableConfig={tableConfig}
          isListLoader={isListLoader}
          isList={projectLists}
        />
      </div>
      {projectLists && projectLists.length > 0 && isListLoader === false && (
        <Pagination
          total={isPagination.totalPages}
          current={isPagination.currentPage}
          pagination={(crPage) => handlePagination(crPage)}
        />
      )}
      {projectLists && projectLists.length === 0 && isListLoader === false && (
        <>
          <NoDataFound />
        </>
      )}
    </div>
  );
};

export default ProjectListingRootComponent;
